/**
 * Analytics Service
 * Handles dispatching events to multiple providers (Meta Pixel, Google
 * Analytics, OpenAI / ChatGPT pixel).
 * Safe to use even if tracking scripts are blocked/missing.
 */

const DEBUG = process.env.NODE_ENV !== 'production';

/**
 * Build a deduplication id for an event.
 *
 * Some events are reported twice on purpose: once from the browser (fast, but
 * lost whenever the pixel is blocked) and once from our server (reliable, but
 * missing the browser's cookies). Meta collapses the pair into a single
 * conversion only when both carry the same event name AND the same eventID —
 * otherwise the same signup is counted as two, and every cost-per-acquisition
 * number derived from it is understated by half.
 *
 * Pass the returned id to the backend alongside the request that triggers the
 * server-side event.
 */
export function createEventId(eventName) {
    const unique = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    return `${eventName}.${unique}`;
}

/**
 * OpenAI / ChatGPT pixel
 * ----------------------
 * The pixel measures a fixed vocabulary (`oaiq("measure", name, props,
 * options)`) rather than Meta's event names, and its SDK drops anything it
 * does not recognise ("validation failed; event dropped" in the console). So
 * Meta events are translated here, and only the conversions an ad can be
 * credited with are forwarded:
 *
 *   CompleteRegistration -> registration_completed
 *   InitiateCheckout     -> checkout_started
 *   Subscribe            -> subscription_created
 *
 * Purchase is deliberately absent. On this site every purchase is a
 * subscription, and it is always reported together with a Subscribe for the
 * same payment; forwarding both would count one payment as two conversions.
 * subscription_created is the one kept because its schema carries the plan id,
 * which is the one fact the success page knows. If a purchase that is not a
 * subscription is ever tracked, map it to order_created here.
 *
 * Custom events (trackCustom) are not forwarded either. The only one, Login,
 * is activity rather than a conversion: it says nothing about which ad earned
 * a customer, and would ship every sign-in to a third party for no return.
 *
 * Rules the SDK enforces (bzrcdn.openai.com/sdk/oaiq.min.js, v0.1.41):
 *   - `props.type` must name the event's schema family, or the event is dropped.
 *   - `amount` is an integer in the currency's minor units (2599 = 25.99) and
 *     must travel with a three-letter `currency`.
 *   - Fields outside the family are warned about; nothing else is dropped.
 *   - `event_id` in the options is the deduplication key, as eventID is for Meta.
 */
const OPENAI_EVENTS = {
    CompleteRegistration: { name: 'registration_completed', type: 'customer_action' },
    InitiateCheckout: { name: 'checkout_started', type: 'contents' },
    Subscribe: { name: 'subscription_created', type: 'plan_enrollment' },
};

/** The fields each schema family accepts besides `type`. */
const OPENAI_FAMILY_FIELDS = {
    customer_action: ['amount', 'currency'],
    contents: ['amount', 'currency', 'contents'],
    plan_enrollment: ['plan_id', 'amount', 'currency', 'contents'],
};

const CURRENCY_CODE = /^[A-Za-z]{3}$/;

// ISO 4217 minor-unit digits. Everything this app bills in (USD, ILS) has two;
// the zero-decimal currencies are listed so a price in one of them is never
// sent a hundred times too large.
const ZERO_DECIMAL_CURRENCIES = new Set(['JPY', 'KRW']);

function toMinorUnits(value, currency) {
    return Math.round(value * (ZERO_DECIMAL_CURRENCIES.has(currency) ? 1 : 100));
}

/**
 * Money out of Meta-style event data, or nothing. An amount is only reported
 * when the data carries both a real number and a currency: the SDK rejects an
 * amount without a currency, and a guessed amount would corrupt every
 * return-on-ad-spend figure computed from it.
 */
function moneyFrom(data) {
    const hasValue = typeof data.value === 'number'
        || (typeof data.value === 'string' && data.value.trim() !== '');
    const value = Number(data.value);
    const currency = typeof data.currency === 'string' && CURRENCY_CODE.test(data.currency)
        ? data.currency.toUpperCase()
        : null;
    if (!hasValue || !Number.isFinite(value) || !currency) {
        return {};
    }
    return { amount: toMinorUnits(value, currency), currency };
}

/**
 * Translate a Meta-style event into the OpenAI pixel's vocabulary.
 *
 * @param {string} eventName - Meta standard event name
 * @param {object} data - the event data handed to trackEvent
 * @returns {{name: string, props: object} | null} null when the event is not
 *          one the pixel should hear about
 */
export function toOpenAIEvent(eventName, data = {}) {
    const mapping = OPENAI_EVENTS[eventName];
    if (!mapping) {
        return null;
    }

    const candidate = moneyFrom(data);
    if (data.plan_id !== undefined && data.plan_id !== null) {
        candidate.plan_id = String(data.plan_id);
    }
    if (Array.isArray(data.content_ids) && data.content_ids.length) {
        candidate.contents = data.content_ids.map((id) => {
            const item = { id: String(id) };
            if (data.content_name) {
                item.name = String(data.content_name);
            }
            return item;
        });
    }

    const props = { type: mapping.type };
    for (const field of OPENAI_FAMILY_FIELDS[mapping.type]) {
        if (candidate[field] !== undefined) {
            props[field] = candidate[field];
        }
    }
    return { name: mapping.name, props };
}

export const AnalyticsService = {
    /**
     * Track a standard event (e.g. 'Purchase', 'Lead', 'CompleteRegistration')
     * @param {string} eventName - Standard event name from Facebook/GA taxonomy
     * @param {object} data - Optional event data (value, currency, content_name, etc.)
     * @param {string} [eventId] - Deduplication id shared with the server-side
     *        event. Generated when omitted, and returned either way.
     * @returns {string} the event id used, for forwarding to the backend
     */
    trackEvent(eventName, data = {}, eventId = null) {
        const resolvedEventId = eventId || createEventId(eventName);
        this._log(`Track Event: ${eventName}`, { ...data, eventId: resolvedEventId });

        // Meta Pixel
        if (window.fbq) {
            window.fbq('track', eventName, data, { eventID: resolvedEventId });
        }

        // Google Analytics 4 (using gtag)
        if (window.gtag) {
            // Map standard Facebook events to GA4 events where appropriate, or pass through
            // Ideally you'd have a mapping layer, but for now we pass standard names
            window.gtag('event', eventName, data);
        }

        // OpenAI / ChatGPT pixel. Consent-gated like the others, so it may be
        // absent. Carries the same deduplication id as Meta, under its own key.
        if (window.oaiq) {
            const openAIEvent = toOpenAIEvent(eventName, data);
            if (openAIEvent) {
                window.oaiq('measure', openAIEvent.name, openAIEvent.props, { event_id: resolvedEventId });
            }
        }

        return resolvedEventId;
    },

    /**
     * Track a custom event
     * @param {string} eventName - Custom event name
     * @param {object} data - Event data
     * @param {string} [eventId] - Deduplication id, as per trackEvent
     * @returns {string} the event id used
     */
    trackCustom(eventName, data = {}, eventId = null) {
        const resolvedEventId = eventId || createEventId(eventName);
        this._log(`Track Custom: ${eventName}`, { ...data, eventId: resolvedEventId });

        // Meta Pixel
        if (window.fbq) {
            window.fbq('trackCustom', eventName, data, { eventID: resolvedEventId });
        }

        // Google Analytics
        if (window.gtag) {
            window.gtag('event', eventName, data);
        }

        // Custom events are not forwarded to the OpenAI pixel on purpose; see
        // OPENAI_EVENTS above.

        return resolvedEventId;
    },

    /**
     * Report a page view to the OpenAI pixel.
     *
     * Every other tracker counts page views by itself: Meta fires `PageView`
     * from its inline snippet, and GA is reconfigured per navigation in the
     * router. The OpenAI SDK does no such thing — it never observes
     * navigation, and `page_viewed` stays a name in its schema table until
     * something measures it. Without this call the pixel sends nothing but its
     * own start-up ping, which is why the Ads portal counted zero events while
     * the pixel was, by every other measure, installed and working.
     *
     * The first page of a visit is measured by the inline snippet in the
     * document head, so the router calls this only for the client-side
     * navigations that follow.
     *
     * @param {string} path - the path navigated to. Recorded in the debug log
     *        only: the SDK reads the URL from the document itself.
     */
    trackPageView(path) {
        this._log('Page view', { path });

        if (window.oaiq) {
            window.oaiq('measure', 'page_viewed', { type: 'contents' });
        }
    },

    /**
     * Internal logger
     */
    _log(message, data) {
        if (DEBUG) {
            console.log(`[Analytics] ${message}`, data);
        }
    }
};

export default AnalyticsService;
